import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import SearchBar from "./SearchBar";
import ActionButtons from "./ActionButtons";
import DeviceTable from "./DeviceTable";
import { changeFriendlyName, changeTagsForDevice, confirmNebulaChange, connectComputer, deleteDevice, deviceConnectUserName, getAllDeviceAccessInfo, getComputers, getComputersCount, getConfig, getRolesList, performScanforDevice, updateGateway } from "./Api/EndpointApi";

import useUserStore from "../../../services/userStore";
import { Computer, Device, DeviceLoadingState, RecordingTogglePayload, Tag } from "../interfaces/EndpointInterfaces";
import { Monitor, Plus, Search } from "lucide-react";
import { Button } from "../../ui/button";
import AddManualDevice from "./AddManualDevice";
import { useToast } from "../../../hooks/use-toast";
import { Role } from "../interfaces/CommonInterfaces";
import { createGuacConnection, toggleRecordingAPI, toggleRecordingStatus } from "./Api/computerApi";
import { transformApiToDevice } from "./helper";
import { myLocalStorage } from "../../StorageHelper";
import { checkIfUserHassAccess } from "../../../services/Helpers";
import useDebounce from "../../../utils/useDebounce";
import TenantLists from "../TenantLists";
import { Input } from "../../ui/input";
import TenantDeviceTable from "../TenantUserEndpoints/TenantDeviceTable";
import "../TenantUserEndpoints/TenantEndpoints.css";
import PlatformFilter, { PlatformFilterValue } from "./PlatformFilter";
import "./Endpoints.css";
import { getDevicePlatform } from "./devicePlatform";
import useNotificationStore from "../../../services/notificationStore";
import {
    LIST_STATE_KEYS,
    parsePersistedNumber,
    parsePersistedString,
    readPersistedListState,
    writePersistedListState,
    useSkipInitialPageReset,
} from "../../../utils/listPaginationState";




export default function DeviceManagementDashboard() {
  const user = useUserStore((state) => state.user) || {};
  const role = user.role || myLocalStorage.getItem("role");
  const email = user.email || myLocalStorage.getItem("email");
  const userId = user.userId;
  const userRole = user.userRole;
  const savedEndpointState = readPersistedListState(LIST_STATE_KEYS.endpoints);

  const [searchQuery, setSearchQuery] = useState(parsePersistedString(savedEndpointState.searchQuery));
  const [selectedTenant, setSelectedTenant] = useState(() =>
    parsePersistedString(savedEndpointState.selectedTenant)
    || myLocalStorage.getItem("latestTenant")?.tenantName
    || "",
  );
  const [, setSelectedDevices] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [deviceList, setDeviceList] = useState<Device[]>([]);
  const [page, setPage] = useState<number>(parsePersistedNumber(savedEndpointState.page, 1));
  const [rowsPerPage, setRowsPerPage] = useState<number>(parsePersistedNumber(savedEndpointState.rowsPerPage, 15));
  const runTenantChangeWithPageReset = useSkipInitialPageReset(setPage);
  const [showAddDeviceDialog, setShowAddDeviceDialog] = useState(false);
  const [lastSelectedDevice, setLastSelectedDevice] = useState<Device | null>(null);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [totalDevicesCount, setTotalDevicesCount] = useState<number>(0);
  const [platformFilter, setPlatformFilter] = useState<PlatformFilterValue>(null);
  const [groupByPlatform, setGroupByPlatform] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const setSessionTab = useUserStore((state) => state.setSessionTab);
  const [adminAccess, setAdminAccess] = useState("ALL_MACHINES");
  const { toast } = useToast();
  const [deviceLoadingState, setDeviceLoadingState] = useState<Record<string, DeviceLoadingState>>({});
  const { userApprovedNotifications } =
    useNotificationStore();
  const deviceListRequestRef = useRef(0);
  const deviceCountRequestRef = useRef(0);
  const deviceToComputer = (device: Device): Computer => ({
    id: device.id,
    guachost: device.guachost ?? "",
    OperatingSystem: device.os ?? device.OperatingSystem ?? "",
    accessRemainingTime: device.accessRemainingTime,
    whiteswanAccessGroupId: device.whiteswanAccessGroupId,
    profileMode: device.profileMode,
    mode: device.mode,
  });


  const fetchDeviceCount = async () => {
    const requestId = ++deviceCountRequestRef.current;
    try {
      let tenantName = selectedTenant || myLocalStorage.getItem("latestTenant")?.tenantName;

      const response = await getComputersCount({
        tenantName: tenantName,
        email,
        role,
        userId,
        searchMachine: searchQuery,
      });
      if (requestId === deviceCountRequestRef.current) {
        setTotalDevicesCount(response ?? 0);
      }
    } catch (error) {
      console.error("Failed to fetch devices", error);
      if (requestId === deviceCountRequestRef.current) {
        setTotalDevicesCount(0);
      }
    }
  }



  const checkAccessByGroup = async (deviceList: any, page: number, limit: number, searchQuery: string) => {
    let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;

    const accessInfoList = await getAllDeviceAccessInfo(
      email,
      page,
      limit,
      searchQuery,
      tenantName
    );
    const enrichedDevices = deviceList.map((device: any) => {
      const access = accessInfoList.find((a: any) => a.deviceId === device.id);
      if (
        access &&
        checkIfUserHassAccess(access?.adComputerUserGroupMembership)
      ) {
        return {
          ...device,
          hasAccess: true,
          whiteswanAccessGroupId: access?.adComputerUserGroupMembership?.id,
          userId: userId,
          isWhiteswanAccess: true,
          accessRemainingTime:
            access?.adComputerUserGroupMembership?.remainingTime,
        };
      }

      return {
        ...device,
        whiteswanAccessGroupId: access?.adComputerUserGroupMembership?.id,
        isWhiteswanAccess: true,
      };
    });
    return enrichedDevices;
  }

  const fetchDeviceList = useCallback(async (isFirst = false, from = "1") => {
    const requestId = ++deviceListRequestRef.current;
    const isLatestRequest = () => requestId === deviceListRequestRef.current;
    if (isFirst) setLoading(true);
    try {
      let tenantName = selectedTenant || myLocalStorage.getItem("latestTenant")?.tenantName;
      const response = await getComputers({
        tenantName: tenantName,
        email,
        role,
        userId,
        page: page - 1,
        limit: rowsPerPage,
        searchMachine: searchQuery,
      });
      const devicesSortedById = response.sort((a, b) =>
        a.id > b.id ? 1 : -1,
      );

      if (role === "TENANT_USER") {

        const newDeviceList = await checkAccessByGroup(
          devicesSortedById,
          page - 1,
          rowsPerPage,
          searchQuery,
        );
        if (!isLatestRequest()) return;
        setDeviceList(transformApiToDevice(newDeviceList));
      } else {
        if (!isLatestRequest()) return;
        setDeviceList(transformApiToDevice(devicesSortedById));
      }



    } catch (error) {
      console.error("Failed to fetch devices", error);
      if (isLatestRequest()) setDeviceList([]);
    } finally {
      if (isLatestRequest()) setLoading(false);
    }
  }, [selectedTenant, email, role, userId, page, searchQuery, rowsPerPage]);


  const getRoles = async () => {
    const response = await getRolesList(selectedTenant);

    if (response && response.length > 0) {
      const formattedRoles: Role[] = response
        .map((role: { id: number; roleName: string }) => ({
          id: String(role.id),
          name: role.roleName,
        }))
        .filter((role: Role) =>
          role.name !== "epam-user" &&
          role.name !== "super-user" &&
          role.name !== "unassigned-user"
        );

      setRoles(formattedRoles);
    } else {
      setRoles([]);
    }
  };

  const getGeneralSettings = async (name: string) => {
    try {
      const response = await getConfig(selectedTenant);
      if (!response) {
        setAdminAccess("ALL_MACHINES");
        return;
      }
      const configValue = response.find(
        (config: { settingsName: string; settingsValue?: string }) =>
          config.settingsName === name
      );

      if (configValue?.settingsValue) {
        setAdminAccess(configValue.settingsValue);
      } else {
        setAdminAccess("ALL_MACHINES");
      }
    } catch (error) {
      console.error("Failed to fetch general settings", error);
    }
  }


  const updateFriendlyName = async (deviceId: string, friendlyName: string, lastSelectedDevice: Device | null) => {
    const response = await changeFriendlyName({
      tenantName: selectedTenant,
      deviceId,
      friendlyName
    });
    if (response) {
      setLastSelectedDevice(null)
      fetchDeviceList(false, "2");
      toast({ title: "Friendly Name Updated", description: `Device "${lastSelectedDevice?.name}" friendly name updated successfully.` });

    }
  }

  const updateNebula = async (deviceId: string, enableNebula: boolean, lastSelectedDevice: Device | null) => {
    const response = await confirmNebulaChange({
      deviceId,
      enableNebula
    });
    if (response) {
      toast({ title: "Nebula Status Updated", description: `Device "${lastSelectedDevice?.name}" nebula status updated successfully.` });
      setLastSelectedDevice(null)
    }
    fetchDeviceList(false, "11");
    setLastSelectedDevice(null);
  }


  const setGateway = async (deviceId: string, selectedSubnets: string[], label: string) => {

    const response = await updateGateway({
      deviceId,
      selectedSubnets
    });
    if (response) {
      setLastSelectedDevice(null)
      toast({ title: label && label.length === 0 ? "Configured Subnet " : label, description: `Device "${lastSelectedDevice?.name}"` });

    }


    fetchDeviceList(false, "10");
    setLastSelectedDevice(null);
  }


  const updateTags = async (deviceId: string, existingTags: Tag[], newTags: Tag[]) => {
    try {
      await changeTagsForDevice({
        deviceId,
        existingTags,
        newTags,
        tenantName: selectedTenant,
      });

      fetchDeviceList(false, "9");
      toast({ title: "Tags Updated", description: `Device "${lastSelectedDevice?.name}" tags updated successfully.` });

    } catch (error) {
      console.error("Error updating tags:", error);
    }
  }

  const performScan = async (deviceId: string,) => {
    const response = await performScanforDevice(deviceId);
    if (response) {
      toast({ title: "Scan Progress", description: `Device "${lastSelectedDevice?.name}" scan started successfully.` });

    }
    setLastSelectedDevice(null);
  }

  const deleteMachine = async (deviceId: string, lastSelectedDevice: Device | null) => {
    const response = await deleteDevice(deviceId);
    if (response) {
      toast({ title: "Device Deleted", description: `Device "${lastSelectedDevice?.name}" deleted successfully.` });
      setLastSelectedDevice(null);
      fetchDeviceList(false, "8");
    }
    setLastSelectedDevice(null);
  }

  const updateDeviceConnectUsername = async (userId: number, username: string, role: string, deviceId: string, port: number) => {
    try {
      const response = await deviceConnectUserName({
        userId,
        username,
        role,
        deviceId,
        accessPort: String(port)
      });
      if (response) {
        fetchDeviceList(false, "7");
      }
    } catch (error) {
      console.error("Failed to update device connect username", error);
    }
  }


  const handleConnect = async (device: Device, mode: string, label: string) => {

    let deviceId = device?.id;
    setDeviceLoadingState((prevState: any) => ({
      ...prevState,
      [deviceId]: {
        loading: true,
        message: "Connecting... Please wait."
      }
    }));
    const computer: Computer = deviceToComputer(device);


    try {
      const userType = role === "TENANT_USER" ? "user" : "admin";
      await connectComputer(computer, email, userType, mode, label, userId, setSessionTab, userApprovedNotifications);
      setDeviceLoadingState((prevState: any) => ({
        ...prevState,
        [deviceId]: {
          loading: false,
          message: "Redirecting... Please wait."
        }
      }));
    } catch (error) {
      console.error("Failed to connect:", error);
      setDeviceLoadingState((prevState: any) => ({
        ...prevState,
        [deviceId]: {
          loading: false,
          message: "Connection failed. Please try again."
        }
      }));
    }

  };

  const handleRecordingToggle = async (payload: RecordingTogglePayload) => {
    let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;

    const computer: Computer = deviceToComputer(payload.device!);
    const connectionData = await createGuacConnection(computer, "", "", "admin");
    if (!connectionData) return;
    await toggleRecordingAPI(payload, connectionData);
    await toggleRecordingStatus(payload.device!.id, tenantName);
    fetchDeviceList(false, "6");
  };


  useEffect(() => {
    if (!email || selectedTenant) return;

    const syncTenantFromStorage = () => {
      const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;
      if (tenantName) {
        setSelectedTenant(tenantName);
        return true;
      }
      return false;
    };

    if (syncTenantFromStorage()) return;

    const intervalId = window.setInterval(() => {
      if (syncTenantFromStorage()) {
        window.clearInterval(intervalId);
      }
    }, 200);

    return () => window.clearInterval(intervalId);
  }, [email, selectedTenant]);

  useEffect(() => {
    if (!email) {
      setLoading(false);
      return;
    }

    const tenant = selectedTenant || myLocalStorage.getItem("latestTenant")?.tenantName;
    if (!tenant) {
      return;
    }

    let cancelled = false;

    const loadData = async (showLoader: boolean) => {
      if (showLoader) {
        setLoading(true);
      }
      try {
        if (role === "TENANT_USER") {
          await Promise.all([
            fetchDeviceList(false, "4"),
            fetchDeviceCount(),
          ]);
        } else {
          await Promise.all([
            fetchDeviceList(false, "5"),
            fetchDeviceCount(),
            getRoles(),
          ]);
        }
        getGeneralSettings("endpointConfig");
      } finally {
        if (!cancelled && showLoader) {
          setLoading(false);
        }
      }
    };

    loadData(true);

    const interval = setInterval(() => {
      loadData(false);
    }, 10000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [selectedTenant, email, role, userId, page, searchQuery, rowsPerPage, fetchDeviceList]);

  useEffect(() => {
    if (email && !selectedTenant && !myLocalStorage.getItem("latestTenant")?.tenantName) {
      const timeout = setTimeout(() => setLoading(false), 20000);
      return () => clearTimeout(timeout);
    }
  }, [email, selectedTenant]);

  useEffect(() => {
    writePersistedListState(LIST_STATE_KEYS.endpoints, {
      page,
      rowsPerPage,
      searchQuery,
      selectedTenant,
    });
  }, [page, rowsPerPage, searchQuery, selectedTenant]);



  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleTenantChange = (tenant: string) => {
    runTenantChangeWithPageReset(() => setSelectedTenant(tenant));
  };

  const handleDeviceSelect = (deviceId: string, selected: boolean) => {
    setSelectedDevices((prev) =>
      selected ? [...prev, deviceId] : prev.filter((id) => id !== deviceId)
    );

    if (selected) {
      const device = deviceList.find((d) => d.id === deviceId) || null;
      setLastSelectedDevice(device);
    } else if (lastSelectedDevice?.id === deviceId) {
      setLastSelectedDevice(null);
      setShowSelectedOnly(false);
    }
  };

  useEffect(() => {
    if (!lastSelectedDevice && showSelectedOnly) {
      setShowSelectedOnly(false);
    }
  }, [lastSelectedDevice, showSelectedOnly]);


  const debouncedSearchQuery = useDebounce(searchQuery, 500);  // Use debouncing
  const filteredDevices = useMemo(() => {
    return deviceList.filter(
      (device) =>
        device.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        device.os.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        device.ipAddresses.some((ip) => ip.includes(debouncedSearchQuery)) ||
        device.tags.some((tag) => tag.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()))
    );
  }, [deviceList, debouncedSearchQuery]);

  const tenantDevices = useMemo(
    () => (platformFilter ? filteredDevices.filter((device) => getDevicePlatform(device) === platformFilter) : filteredDevices),
    [filteredDevices, platformFilter],
  );




  return (
    <>
      {role === "TENANT_USER" ?
        <>

          <div className="tenant-endpoints-page">
            <div className="tenant-endpoints-toolbar">
              <div className="tenant-endpoints-summary">
                <span className="tenant-endpoints-icon">
                  <Monitor aria-hidden="true" />
                </span>
                <div>
                  <strong data-testid="text-platform-logs-title">Available Devices</strong>
                  <span>
                    {loading
                      ? "Loading devices..."
                      : `${totalDevicesCount} device${totalDevicesCount !== 1 ? "s" : ""} assigned to your roles`}
                  </span>
                </div>
              </div>
              <div className="tenant-endpoints-actions">
                <TenantLists onSelectorChange={handleTenantChange} isLabelNeeded={false} variant="card" />
                <div className="tenant-endpoints-search">
                  <Search aria-hidden="true" />
                  <Input
                    placeholder="Search devices..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    data-testid="input-search-devices"
                  />
                </div>
                <PlatformFilter value={platformFilter} onChange={setPlatformFilter} />
              </div>
            </div>

            <TenantDeviceTable
              isLoading={loading}
              devices={tenantDevices}
              onConnect={handleConnect}
              totalDevicesCount={totalDevicesCount}
              deviceLoadingState={deviceLoadingState}
              setPage={setPage}
              page={page}
            />
          </div>
        </>
        :

        <div className="endpoints-page w-full min-w-0 p-6" data-testid="device-management-dashboard">
          <div className="min-w-0 space-y-6">

            <div className="endpoint-elevated-card relative z-50 w-full flex items-center p-3 rounded-2xl bg-card backdrop-blur-xl border border-border/50 gap-4 lg:gap-6 flex-wrap">
              <SearchBar
                placeholder="Search..."
                onSearch={handleSearch}
                onSelectorChange={handleTenantChange}
                lastSelectedDevice={lastSelectedDevice}
                onTagsChange={(deviceId, existingTags, newTags) => updateTags(deviceId, existingTags, newTags)}
                resetValue={() => setLastSelectedDevice(null)}
                platformFilter={platformFilter}
                onPlatformFilterChange={setPlatformFilter}
                groupByPlatform={groupByPlatform}
                onGroupByPlatformChange={setGroupByPlatform}
              />


              {(lastSelectedDevice || userRole !== "epam-admin") && (
                <div className="hidden lg:block w-px h-12 bg-gradient-to-b from-transparent via-border to-transparent" />
              )}
              <div className="flex items-center gap-3 ml-auto mt-5 max-w-full [&>div]:flex-wrap [&>div]:gap-y-2">
                {!lastSelectedDevice && userRole !== "epam-admin" && <Button
                  onClick={() => setShowAddDeviceDialog(true)}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all duration-300 shadow-md shadow-primary/20 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/30"
                  data-testid="button-add-device"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-[13px] tracking-wide uppercase">Add Device</span>
                </Button>}
                {lastSelectedDevice && <ActionButtons
                  resetValue={() => setLastSelectedDevice(null)}
                  onFriendlyName={(deviceId, friendlyName) => updateFriendlyName(deviceId, friendlyName, lastSelectedDevice)}
                  onDisableConnectivity={(enabled) => updateNebula(lastSelectedDevice.id, enabled, lastSelectedDevice)}
                  onScan={() => performScan(lastSelectedDevice.id)}
                  onGateway={(deviceId, selectedSubnets, label) => setGateway(deviceId, selectedSubnets, label)}
                  onDelete={() => deleteMachine(lastSelectedDevice.id, lastSelectedDevice)}
                  tenantName={selectedTenant || myLocalStorage.getItem("latestTenant")?.tenantName || ""}
                  enabledButtons={{
                    friendlyName: !!lastSelectedDevice,
                    disableConnectivity: !!lastSelectedDevice,
                    scan: !!lastSelectedDevice,
                    delete: !!lastSelectedDevice && lastSelectedDevice.agentVersion !== '1.3.2',
                    gateway: !!lastSelectedDevice && lastSelectedDevice.os.toLowerCase().includes("linux"),
                    tags: !!lastSelectedDevice,
                  }}
                  selectedDevice={lastSelectedDevice}
                />}

              </div>

            </div>
            {role !== "TENANT_USER" && <DeviceTable
              isLoading={loading}
              devices={filteredDevices}
              onConnect={handleConnect}
              onDeviceSelect={handleDeviceSelect}
              selectedTenant={selectedTenant}
              onRecordingToggle={handleRecordingToggle}
              totalDevicesCount={totalDevicesCount}
              adminAccess={adminAccess}
              page={page}
              rowsPerPage={rowsPerPage}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
              configureDeviceUsername={(userId, username, role, deviceId, port) => updateDeviceConnectUsername(userId, username, role, deviceId, port)}
              fetchDeviceList={fetchDeviceList}
              fetchDeviceCount={fetchDeviceCount}
              roles={roles}
              deviceLoadingState={deviceLoadingState}
              lastSelectedDevice={lastSelectedDevice}
              platformFilter={platformFilter}
              groupByPlatform={groupByPlatform}
            />}
          </div>
          <AddManualDevice
            open={showAddDeviceDialog}
            onOpenChange={setShowAddDeviceDialog}
            updateEndpoints={() => { }}
          />
        </div>
      }
    </>
  );
}
